import { BotonExportar } from '@/components/dominio/BotonExportar';
import { ComentarioSeccion } from '@/components/dominio/ComentarioSeccion';
import { KpiCard } from '@/components/dominio/KpiCard';
import { MontoCRC } from '@/components/dominio/MontoCRC';
import { SelectorPeriodo } from '@/components/dominio/SelectorPeriodo';
import { SortableHeader, useSortColumn } from '@/components/dominio/SortableHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePeriodo } from '@/hooks/usePeriodo';
import { useIngresos } from '@/hooks/useUtilidades';
import { formatearPeriodo, mesAnclaPeriodo } from '@adelante/shared';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Fragment, useMemo, useRef, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

function extraerBloque(lote: string): string {
  const idx = lote.indexOf('.');
  return idx > 0 ? lote.slice(0, idx) : lote;
}

const MESES_CORTOS = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

function abreviarCRC(valor: number): string {
  if (Math.abs(valor) >= 1e9) return `₡${(valor / 1e9).toFixed(1)}bn`;
  if (Math.abs(valor) >= 1e6) return `₡${(valor / 1e6).toFixed(0)}M`;
  return `₡${valor.toLocaleString('es-CR')}`;
}

function formatearPorcentaje(valor: number | null): string {
  if (valor === null) return '—';
  return `${(valor * 100).toFixed(1)}%`;
}

type GrupoLote = {
  bloque: string;
  sumaIngresos: number;
  sumaIngresoNetoAD: number;
  sumaUtilidad: number;
};

type ModoIngreso = 'bruto' | 'neto';

export function IngresosUtilidadPantalla() {
  const { periodo, setModo, setAnio, setMes, setTrimestre, setDesde, setHasta } = usePeriodo();
  const [expandidosLote, setExpandidosLote] = useState<Set<string>>(new Set());
  const [modoIngreso, setModoIngreso] = useState<ModoIngreso>('neto');
  const { data, isLoading, error } = useIngresos(periodo);
  const refContenido = useRef<HTMLDivElement>(null);

  // Helper para tomar el monto según el modo seleccionado
  const tomarIngreso = (bruto: number, neto: number) => (modoIngreso === 'bruto' ? bruto : neto);

  const etiquetaPeriodo = formatearPeriodo(periodo);
  const ancla = mesAnclaPeriodo(periodo);

  const sortLote = useSortColumn<GrupoLote>(null);

  function toggleBloque(bloque: string) {
    setExpandidosLote((prev) => {
      const next = new Set(prev);
      if (next.has(bloque)) next.delete(bloque);
      else next.add(bloque);
      return next;
    });
  }

  const gruposLote = useMemo(() => {
    if (!data) return [];
    const map = new Map<string, typeof data.porLote>();
    for (const fila of data.porLote) {
      const bloque = extraerBloque(fila.lote ?? '(sin lote)');
      if (!map.has(bloque)) map.set(bloque, []);
      map.get(bloque)!.push(fila);
    }
    return Array.from(map.entries()).map(([bloque, lotes]) => ({
      bloque,
      lotes,
      sumaIngresos: lotes.reduce((acc, l) => acc + Number(l.ingresos), 0),
      sumaIngresoNetoAD: lotes.reduce((acc, l) => acc + Number(l.ingreso_neto_ad), 0),
      sumaUtilidad: lotes.reduce((acc, l) => acc + Number(l.utilidad), 0),
    }));
  }, [data]);

  const gruposOrdenados = useMemo(
    () => sortLote.sortData(gruposLote),
    [gruposLote, sortLote.sortData],
  );

  // Hooks de exportación PDF: expandir todos los bloques con múltiples lotes
  // antes de capturar, para que el detalle aparezca en el PDF.
  const expandidosPreviosRef = useRef<Set<string> | null>(null);
  function antesDeExportarPDF() {
    expandidosPreviosRef.current = expandidosLote;
    const todos = new Set<string>();
    for (const g of gruposOrdenados) {
      if (g.lotes.length > 1) todos.add(g.bloque);
    }
    setExpandidosLote(todos);
  }
  function despuesDeExportarPDF() {
    if (expandidosPreviosRef.current) {
      setExpandidosLote(expandidosPreviosRef.current);
      expandidosPreviosRef.current = null;
    }
  }

  const datosEvolucion = data
    ? data.evolucionComparada.map((f) => ({
        periodo: `${MESES_CORTOS[f.mes - 1]} ${String(f.anio).slice(2)}`,
        ingreso: tomarIngreso(Number(f.ingresos), Number(f.ingreso_neto_ad)),
        utilidad: Number(f.utilidad),
      }))
    : [];

  return (
    <div ref={refContenido} className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Ingresos y Utilidad</h1>
          <p className="mt-1 text-sm text-muted-foreground">{etiquetaPeriodo}</p>
        </div>
        <div data-pdf-ignore className="flex flex-wrap items-start gap-3">
          <SelectorPeriodo
            periodo={periodo}
            onSetModo={setModo}
            onSetAnio={setAnio}
            onSetMes={setMes}
            onSetTrimestre={setTrimestre}
            onSetDesde={setDesde}
            onSetHasta={setHasta}
          />
          <BotonExportar
            titulo={`Ingresos ${modoIngreso === 'bruto' ? 'Brutos' : 'Netos AD'} y Utilidad`}
            subtitulo={etiquetaPeriodo}
            refContenido={refContenido}
            disabled={!data}
            onAntesExportarPDF={antesDeExportarPDF}
            onDespuesExportarPDF={despuesDeExportarPDF}
            tablas={
              data
                ? [
                    {
                      nombre: 'Por lote',
                      encabezados: [
                        'Lote',
                        modoIngreso === 'bruto' ? 'Ingreso Bruto (₡)' : 'Ingreso Neto AD (₡)',
                        'Utilidad (₡)',
                      ],
                      filas: data.porLote.map((f) => [
                        f.lote ?? '(sin lote)',
                        modoIngreso === 'bruto' ? Number(f.ingresos) : Number(f.ingreso_neto_ad),
                        Number(f.utilidad),
                      ]),
                    },
                    {
                      nombre: 'Evolución comparada',
                      encabezados: [
                        'Año',
                        'Mes',
                        modoIngreso === 'bruto' ? 'Ingreso Bruto (₡)' : 'Ingreso Neto AD (₡)',
                        'Utilidad (₡)',
                      ],
                      filas: data.evolucionComparada.map((f) => [
                        f.anio,
                        f.mes,
                        modoIngreso === 'bruto' ? Number(f.ingresos) : Number(f.ingreso_neto_ad),
                        Number(f.utilidad),
                      ]),
                    },
                  ]
                : []
            }
          />
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Cargando…
        </div>
      )}

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6 text-destructive">{error.message}</CardContent>
        </Card>
      )}

      {data && (
        <>
          {/* Toggle Bruto / Neto AD */}
          <div data-pdf-ignore className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Ingreso:
            </span>
            <div className="inline-flex rounded-md border border-input p-0.5">
              <button
                type="button"
                onClick={() => setModoIngreso('bruto')}
                className={
                  modoIngreso === 'bruto'
                    ? 'rounded bg-adelante-primary px-3 py-1 text-xs font-semibold text-adelante-foreground'
                    : 'rounded px-3 py-1 text-xs text-muted-foreground hover:text-foreground'
                }
              >
                Bruto bancario
              </button>
              <button
                type="button"
                onClick={() => setModoIngreso('neto')}
                className={
                  modoIngreso === 'neto'
                    ? 'rounded bg-adelante-primary px-3 py-1 text-xs font-semibold text-adelante-foreground'
                    : 'rounded px-3 py-1 text-xs text-muted-foreground hover:text-foreground'
                }
              >
                Neto AD (s/ GM, QFI y comisión)
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Período actual
              </p>
              <div className="grid grid-cols-3 gap-3">
                <KpiCard
                  titulo={modoIngreso === 'bruto' ? 'Ingreso Bruto' : 'Ingreso Neto AD'}
                  monto={tomarIngreso(data.kpisActual.ingresos, data.kpisActual.ingreso_neto_ad)}
                />
                <KpiCard titulo="Utilidad" monto={data.kpisActual.utilidad} />
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                      % Utilidad
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <span className="font-mono text-2xl font-semibold tabular-nums">
                      {formatearPorcentaje(data.kpisActual.porcentaje)}
                    </span>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Período anterior
              </p>
              <div className="grid grid-cols-3 gap-3">
                <KpiCard
                  titulo={modoIngreso === 'bruto' ? 'Ingreso Bruto' : 'Ingreso Neto AD'}
                  monto={tomarIngreso(
                    data.kpisAnterior.ingresos,
                    data.kpisAnterior.ingreso_neto_ad,
                  )}
                />
                <KpiCard titulo="Utilidad" monto={data.kpisAnterior.utilidad} />
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                      % Utilidad
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <span className="font-mono text-2xl font-semibold tabular-nums">
                      {formatearPorcentaje(data.kpisAnterior.porcentaje)}
                    </span>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Evolución comparada (24 meses)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={datosEvolucion} margin={{ left: 8, right: 16, top: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="periodo"
                    tick={{ fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    interval={2}
                  />
                  <YAxis
                    tickFormatter={abreviarCRC}
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip formatter={(v) => [abreviarCRC(Number(v)), '']} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="ingreso"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={false}
                    name={modoIngreso === 'bruto' ? 'Ingreso Bruto' : 'Ingreso Neto AD'}
                  />
                  <Line
                    type="monotone"
                    dataKey="utilidad"
                    stroke="#7c3aed"
                    strokeWidth={2}
                    dot={false}
                    name="Utilidad"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Por lote</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-64 overflow-y-auto px-6 pb-4">
                <Table>
                  <TableHeader className="sticky top-0 bg-card">
                    <TableRow>
                      <TableHead className="w-6" />
                      <TableHead>
                        <SortableHeader
                          label="Bloque / Lote"
                          sortKey="bloque"
                          activeSortKey={sortLote.sortKey}
                          direction={sortLote.direction}
                          onSort={sortLote.handleSort}
                        />
                      </TableHead>
                      <TableHead className="text-right">
                        <SortableHeader
                          label={modoIngreso === 'bruto' ? 'Ingreso Bruto' : 'Ingreso Neto AD'}
                          sortKey={modoIngreso === 'bruto' ? 'sumaIngresos' : 'sumaIngresoNetoAD'}
                          activeSortKey={sortLote.sortKey}
                          direction={sortLote.direction}
                          onSort={sortLote.handleSort}
                          align="right"
                        />
                      </TableHead>
                      <TableHead className="text-right">
                        <SortableHeader
                          label="Utilidad"
                          sortKey="sumaUtilidad"
                          activeSortKey={sortLote.sortKey}
                          direction={sortLote.direction}
                          onSort={sortLote.handleSort}
                          align="right"
                        />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {gruposOrdenados.map((grupo) => {
                      const tieneMultiples = grupo.lotes.length > 1;
                      const expandido = expandidosLote.has(grupo.bloque);
                      return (
                        <Fragment key={grupo.bloque}>
                          <TableRow
                            className={
                              tieneMultiples
                                ? 'cursor-pointer select-none hover:bg-muted/40'
                                : undefined
                            }
                            onClick={tieneMultiples ? () => toggleBloque(grupo.bloque) : undefined}
                          >
                            <TableCell className="text-muted-foreground">
                              {tieneMultiples &&
                                (expandido ? (
                                  <ChevronDown className="h-3.5 w-3.5" />
                                ) : (
                                  <ChevronRight className="h-3.5 w-3.5" />
                                ))}
                            </TableCell>
                            <TableCell className={tieneMultiples ? 'font-semibold' : undefined}>
                              {tieneMultiples
                                ? `Bloque ${grupo.bloque}`
                                : (grupo.lotes[0]!.lote ?? '(sin lote)')}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              <MontoCRC
                                monto={tomarIngreso(grupo.sumaIngresos, grupo.sumaIngresoNetoAD)}
                              />
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              <MontoCRC monto={grupo.sumaUtilidad} />
                            </TableCell>
                          </TableRow>
                          {tieneMultiples &&
                            expandido &&
                            grupo.lotes.map((fila, i) => (
                              <TableRow key={fila.lote ?? i} className="bg-muted/20">
                                <TableCell className="text-center text-xs text-muted-foreground">
                                  ↳
                                </TableCell>
                                <TableCell className="pl-4 text-sm">
                                  {fila.lote ?? '(sin lote)'}
                                </TableCell>
                                <TableCell className="text-right text-sm">
                                  <MontoCRC
                                    monto={tomarIngreso(
                                      Number(fila.ingresos),
                                      Number(fila.ingreso_neto_ad),
                                    )}
                                  />
                                </TableCell>
                                <TableCell className="text-right text-sm">
                                  <MontoCRC monto={Number(fila.utilidad)} />
                                </TableCell>
                              </TableRow>
                            ))}
                        </Fragment>
                      );
                    })}
                    {gruposOrdenados.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground">
                          Sin datos por lote
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <ComentarioSeccion anio={ancla.anio} mes={ancla.mes} scope="seccion" seccionId="ingresos" />
    </div>
  );
}
